import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { CalendarCheck } from 'lucide-react'
import { useBookingsList } from '@/hooks/data/useBookings'
import type { BookingListFilters, BookingStatus } from '@/types/domain'

function statusColor(s: string) {
  return (
    {
      Active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    }[s] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  )
}

function BookingsPage() {
  const [status, setStatus] = useState<BookingStatus | ''>('')
  const [serviceType, setServiceType] = useState('')
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const cursor = cursorStack[cursorStack.length - 1]

  const filters: BookingListFilters = {
    status: status || undefined,
    serviceType: serviceType || undefined,
    cursor,
    pageSize: 20,
  }
  const list = useBookingsList(filters)

  const resetPaging = () => setCursorStack([])

  return (
    <div className="p-2 md:p-4 space-y-4">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
        <CalendarCheck className="w-6 h-6" /> Bookings
      </h1>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as BookingStatus | '')
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          <option value="Active">Active</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Completed">Completed</option>
        </select>
        <select
          value={serviceType}
          onChange={(e) => {
            setServiceType(e.target.value)
            resetPaging()
          }}
          className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
        >
          <option value="">All services</option>
          <option value="Regular">Regular</option>
          <option value="Deep">Deep</option>
          <option value="MoveOut">Move Out</option>
        </select>
      </div>

      {list.isLoading ? (
        <p className="p-6 text-gray-500">Loading bookings…</p>
      ) : list.isError ? (
        <p className="p-6 text-red-600">Failed to load bookings. Requires the manager role.</p>
      ) : list.data && list.data.items.length === 0 ? (
        <p className="p-6 text-gray-500">No bookings match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 font-medium">Service</th>
                <th className="px-4 py-3 font-medium">Suburb</th>
                <th className="px-4 py-3 font-medium">Recurrence</th>
                <th className="px-4 py-3 font-medium">First visit</th>
                <th className="px-4 py-3 font-medium">Price</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((b) => (
                <tr key={b.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3">
                    <Link to="/bookings/$id" params={{ id: b.id }} className="text-blue-600 hover:underline">
                      {b.serviceType}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{b.suburb}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{b.recurrence}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{new Date(b.firstVisitStart).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">${b.agreedPrice.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(b.status)}`}>{b.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center gap-3">
        {cursorStack.length > 0 && (
          <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
            ← Newer
          </button>
        )}
        {list.data?.hasMore && list.data.nextCursor && (
          <button
            onClick={() => setCursorStack((prev) => [...prev, list.data!.nextCursor!])}
            className="text-sm text-blue-600 hover:underline"
          >
            Older →
          </button>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/bookings/')({ component: BookingsPage })
